import React, { useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';

// 脉冲动画
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

// 波形条动画
const waveAnimation = keyframes`
  0%, 100% {
    height: 20%;
  }
  50% {
    height: 100%;
  }
`;

// 容器样式
const WaveformContainer = styled.div<{ $size: number; $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  position: relative;
  border-radius: 50%;
  background: ${props => props.$isActive 
    ? 'linear-gradient(135deg, #10a37f, #1a73e8)' 
    : 'var(--bg-secondary, #f7f7f8)'
  };
  transition: all 0.3s ease;
  
  ${props => props.$isActive && css`
    animation: ${pulse} 2s ease-in-out infinite;
    box-shadow: 
      0 0 20px rgba(16, 163, 127, 0.3),
      0 0 40px rgba(16, 163, 127, 0.2),
      0 0 60px rgba(16, 163, 127, 0.1);
  `}
`;

// 波形条容器
const WaveBars = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 60%;
  width: 60%;
`;

// 单个波形条
const WaveBar = styled.div<{ $height: number; $isActive: boolean; $delay: number }>`
  width: 3px;
  background: ${props => props.$isActive ? '#ffffff' : 'var(--text-secondary, #8e8ea0)'};
  border-radius: 2px;
  transition: height 0.1s ease, background-color 0.3s ease;
  height: ${props => props.$isActive ? `${Math.max(props.$height * 100, 20)}%` : '20%'};
  
  ${props => props.$isActive && css`
    animation: ${waveAnimation} ${1.5 + props.$delay * 0.2}s ease-in-out infinite;
    animation-delay: ${props.$delay * 0.1}s;
  `}
`;

// 中央麦克风图标
const MicrophoneIcon = styled.div<{ $isActive: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 24px;
  color: ${props => props.$isActive ? '#ffffff' : 'var(--primary-color, #10a37f)'};
  transition: color 0.3s ease;
  z-index: 2;
`;

// 音量指示器
const VolumeIndicator = styled.div<{ $level: number; $isVisible: boolean }>`
  position: absolute;
  top: -10px;
  right: -10px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff6b6b, #ff8e53);
  opacity: ${props => props.$isVisible && props.$level > 0.1 ? props.$level : 0};
  transform: scale(${props => 0.5 + props.$level * 0.5});
  transition: all 0.2s ease;
  box-shadow: 0 2px 10px rgba(255, 107, 107, 0.3);
`;

// 录音时长显示
const DurationDisplay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  font-weight: 500;
  color: var(--primary-color, #10a37f);
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 0.3s ease;
  white-space: nowrap;
`;

interface VoiceWaveformProps {
  isRecording: boolean;
  audioLevel?: number;
  duration?: number;
  size?: number;
  showDuration?: boolean;
  className?: string;
}

/**
 * 语音波形动画组件
 * 显示ChatGPT风格的录音动画效果
 */
const VoiceWaveform: React.FC<VoiceWaveformProps> = ({
  isRecording,
  audioLevel = 0,
  duration = 0,
  size = 80,
  showDuration = true,
  className
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  // 格式化时长显示
  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // 生成随机波形高度（模拟音频数据）
  const generateRandomWaveform = (): number[] => {
    const bars = 7;
    const baseHeight = 0.2;
    const waveform: number[] = [];
    
    for (let i = 0; i < bars; i++) {
      if (isRecording) {
        // 录音时根据音量level生成动态波形
        const variation = Math.sin(Date.now() * 0.01 + i) * 0.3;
        const height = baseHeight + (audioLevel * 0.8) + variation;
        waveform.push(Math.max(Math.min(height, 1), baseHeight));
      } else {
        waveform.push(baseHeight);
      }
    }
    
    return waveform;
  };

  // 波形数据
  const waveformData = generateRandomWaveform();

  return (
    <WaveformContainer 
      $size={size} 
      $isActive={isRecording}
      className={className}
    >
      {/* 背景波形条 */}
      <WaveBars>
        {waveformData.map((height, index) => (
          <WaveBar
            key={index}
            $height={height}
            $isActive={isRecording}
            $delay={index}
          />
        ))}
      </WaveBars>

      {/* 麦克风图标 */}
      <MicrophoneIcon $isActive={isRecording}>
        🎤
      </MicrophoneIcon>

      {/* 音量指示器 */}
      <VolumeIndicator 
        $level={audioLevel} 
        $isVisible={isRecording}
      />

      {/* 录音时长 */}
      {showDuration && (
        <DurationDisplay $isVisible={isRecording}>
          {formatDuration(duration)}
        </DurationDisplay>
      )}
    </WaveformContainer>
  );
};

export default VoiceWaveform; 